import { supabaseClient } from "@/lib/supabase";
import type {
	ConsentKey,
	ConsentT,
	ConsentViewT,
} from "@/types/database/consents";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type {
	QueryOptions,
	UseSuspenseQueryOptions,
} from "@tanstack/react-query";

export const consentKeys = {
	all: ["consents"] as const,
	byId: (id: string) => [...consentKeys.all, "byId", id] as const,
	byDiaryId: (diaryId: string) =>
		[...consentKeys.all, "byDiaryId", diaryId] as const,
};

export async function fetchConsentById(
	consentId: string,
): Promise<ConsentViewT | null> {
	const { data, error } = await supabaseClient
		.from("consents_view")
		.select("*")
		.eq("consent_id", consentId)
		.maybeSingle();

	if (error) {
		throw error;
	}

	// If no data is found, return a default consent object

	return data as ConsentViewT | null;
}

type UseConsentByIdOptions = {
	enabled?: boolean;
};

export function useConsentById(
	consentId: string,
	options?: UseConsentByIdOptions,
) {
	return useQuery({
		queryKey: consentKeys.byId(consentId),
		queryFn: () => fetchConsentById(consentId),
		enabled: !!consentId && options?.enabled !== false,
	});
}

export function useSuspenseConsentById(
	consentId: string,
	options?: Omit<
		UseSuspenseQueryOptions<ConsentViewT | null>,
		"queryKey" | "queryFn"
	>,
) {
	return useSuspenseQuery<ConsentViewT | null>({
		queryKey: consentKeys.byId(consentId),
		queryFn: () => fetchConsentById(consentId),
		...options,
	});
}

export function getConsentByIdOptions(consentId: string): Omit<
	QueryOptions<ConsentViewT | null>,
	"queryKey"
> & {
	queryKey: readonly unknown[];
} {
	return {
		queryKey: consentKeys.byId(consentId),
		queryFn: () => fetchConsentById(consentId),
	};
}

/**
 * Fetches all consents for a diary
 */
export async function fetchConsentsByDiaryId(
	diaryId: string,
): Promise<ConsentT[]> {
	const { data, error } = await supabaseClient
		.from("consents")
		.select("*")
		.eq("diary_id", diaryId);

	if (error) {
		throw error;
	}

	return data as ConsentT[];
}

/**
 * Fetches all consents with signatures for a diary
 */
export async function fetchConsentViewsByDiaryId(
	diaryId: string,
): Promise<ConsentViewT[]> {
	const { data, error } = await supabaseClient
		.from("consents_view")
		.select("*")
		.eq("diary_id", diaryId);

	if (error) {
		throw error;
	}

	return data as ConsentViewT[];
}

export function useConsentsByDiaryId(
	diaryId: string,
	options?: UseConsentByIdOptions,
) {
	return useQuery({
		queryKey: consentKeys.byDiaryId(diaryId),
		queryFn: () => fetchConsentViewsByDiaryId(diaryId),
		enabled: !!diaryId && options?.enabled !== false,
	});
}

export function useSuspenseConsentsByDiaryId(
	diaryId: string,
	options?: Omit<
		UseSuspenseQueryOptions<ConsentViewT[]>,
		"queryKey" | "queryFn"
	>,
) {
	return useSuspenseQuery<ConsentViewT[]>({
		queryKey: consentKeys.byDiaryId(diaryId),
		queryFn: () => fetchConsentViewsByDiaryId(diaryId),
		...options,
	});
}
